import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, Zap, RefreshCw, Brain, BarChart2, Clock, Target,
  CheckCircle, Sparkles, Calendar, ThumbsUp, Eye, MousePointer,
  Globe, Play, AlertCircle, ChevronRight, Repeat2, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  'LinkedIn': '#0A66C2', 'Twitter/X': '#1DA1F2', 'Instagram': '#E1306C',
  'TikTok': '#69C9D0', 'Meta (Facebook)': '#1877F2', 'YouTube': '#FF0000',
};

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="llmc-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={13} style={{ color }} />
        </div>
        <span className="text-[11px] font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>{label}</span>
      </div>
      <div className="text-2xl font-black text-white">{value ?? '–'}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</div>}
    </div>
  );
}

export default function PerformanceIntelligence() {
  const [overview, setOverview] = useState(null);
  const [posts, setPosts] = useState([]);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [tracking, setTracking] = useState({});
  const [savingTrack, setSavingTrack] = useState(null);
  const [autopilot, setAutopilot] = useState({ enabled: false, niche: 'AI & Technology', platforms: ['LinkedIn'], posts_per_day: 3, auto_approve: false });
  const [apLoading, setApLoading] = useState(false);
  const [apResult, setApResult] = useState(null);
  const [loadingAP, setLoadingAP] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [ovRes, postsRes, apRes] = await Promise.allSettled([
        axios.get(`${API}/api/performance/overview`),
        axios.get(`${API}/api/scheduler/list?limit=20&status=all`),
        axios.get(`${API}/api/scheduler/autopilot`),
      ]);
      if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data);
      if (postsRes.status === 'fulfilled') setPosts(postsRes.value.data.posts || []);
      if (apRes.status === 'fulfilled') setAutopilot(ap => ({ ...ap, ...apRes.value.data.config }));
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runLoop = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/api/performance/analyze`);
      setReport(data.report);
      loadData();
    } catch {}
    setRunning(false);
  };

  const saveEngagement = async (postId) => {
    const t = tracking[postId];
    if (!t) return;
    setSavingTrack(postId);
    try {
      await axios.put(`${API}/api/scheduler/${postId}`, {
        engagement: { views: +t.views || 0, likes: +t.likes || 0, clicks: +t.clicks || 0, shares: +t.shares || 0 },
      });
      loadData();
    } catch {}
    setSavingTrack(null);
    setTracking(prev => { const n = { ...prev }; delete n[postId]; return n; });
  };

  const saveAutopilot = async () => {
    setApLoading(true);
    try {
      await axios.post(`${API}/api/scheduler/autopilot`, autopilot);
    } catch {}
    setApLoading(false);
  };

  const runAutopilotNow = async () => {
    setLoadingAP(true);
    setApResult(null);
    try {
      const { data } = await axios.post(`${API}/api/scheduler/autopilot/run`);
      setApResult(data);
      loadData();
    } catch (e) {
      setApResult({ success: false, message: 'Failed: ' + (e.response?.data?.detail || e.message) });
    }
    setLoadingAP(false);
  };

  // Aggregate platform breakdown from posts
  const platformBreakdown = posts.reduce((acc, p) => {
    const pl = p.platform || 'Unknown';
    if (!acc[pl]) acc[pl] = 0;
    acc[pl]++;
    return acc;
  }, {});
  const maxPlatformCount = Math.max(...Object.values(platformBreakdown), 1);

  // Aggregate engagement
  const totalViews = posts.reduce((s, p) => s + (p.engagement?.views || 0), 0);
  const totalLikes = posts.reduce((s, p) => s + (p.engagement?.likes || 0), 0);
  const totalClicks = posts.reduce((s, p) => s + (p.engagement?.clicks || 0), 0);
  const avgCTR = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : '0.0';

  const publishedPosts = posts.filter(p => p.status === 'published' || p.status === 'posted');
  const pendingPosts = posts.filter(p => p.status === 'scheduled' || p.status === 'pending_review');

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 p-5 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, rgba(0,212,184,0.25), rgba(61,255,160,0.15))', border: '1px solid rgba(0,212,184,0.4)' }}>
          <TrendingUp size={18} style={{ color: '#00d4b8' }} />
        </div>
        <div>
          <h1 className="text-lg font-black text-white">Performance Intelligence</h1>
          <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>Engagement tracking · Auto-pilot · Einstein optimization</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={runLoop} disabled={running} data-testid="run-autoresearch-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold hover:opacity-80 disabled:opacity-40"
            style={{ background: 'rgba(0,212,184,0.15)', border: '1px solid rgba(0,212,184,0.3)', color: '#00d4b8' }}>
            {running ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
            {running ? 'Analyzing...' : 'Run Einstein Analysis'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-5 pt-3 pb-0 flex-shrink-0" style={{ borderBottom: '1px solid var(--llmc-border)' }}>
        {[
          { id: 'overview', label: 'Overview', icon: BarChart2 },
          { id: 'posts', label: 'Post Tracker', icon: Target },
          { id: 'autopilot', label: 'Auto-Pilot', icon: Zap, badge: autopilot.enabled ? 'ON' : null },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all rounded-t-xl"
            style={{
              background: activeTab === tab.id ? 'rgba(0,212,184,0.08)' : 'transparent',
              borderBottom: activeTab === tab.id ? '2px solid #00d4b8' : '2px solid transparent',
              color: activeTab === tab.id ? '#00d4b8' : 'var(--llmc-text-muted)',
            }}>
            <tab.icon size={12} /> {tab.label}
            {tab.badge && <span className="text-[9px] font-black px-1 py-0.5 rounded" style={{ background: 'rgba(61,255,160,0.15)', color: '#3dffa0' }}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Total Posts" value={posts.length} color="#7c5efa" icon={Calendar} sub={`${publishedPosts.length} published`} />
              <StatCard label="Total Views" value={totalViews > 0 ? totalViews.toLocaleString() : overview?.total_views?.toLocaleString() || '0'} color="#00d4b8" icon={Eye} sub="across all channels" />
              <StatCard label="Total Likes" value={totalLikes > 0 ? totalLikes.toLocaleString() : overview?.total_clicks?.toLocaleString() || '0'} color="#f97316" icon={ThumbsUp} sub="engagement count" />
              <StatCard label="Avg CTR" value={`${overview?.avg_ctr?.toFixed(1) || avgCTR}%`} color="#3dffa0" icon={MousePointer} sub="click-through rate" />
            </div>

            {/* Platform breakdown */}
            <div className="llmc-card p-5">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Globe size={14} style={{ color: '#a68eff' }} /> Platform Breakdown</h2>
              {Object.keys(platformBreakdown).length === 0 ? (
                <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No scheduled posts yet. Schedule posts to see platform breakdown.</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(platformBreakdown).sort((a, b) => b[1] - a[1]).map(([platform, count]) => (
                    <div key={platform} className="flex items-center gap-3">
                      <div className="text-xs font-semibold w-28 truncate" style={{ color: PLATFORM_COLORS[platform] || '#aaa' }}>{platform}</div>
                      <MiniBar value={count} max={maxPlatformCount} color={PLATFORM_COLORS[platform] || '#aaa'} />
                      <div className="text-xs font-bold w-8 text-right text-white">{count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Skill ledger */}
            {overview?.skill_ledger?.length > 0 && (
              <div className="llmc-card p-5">
                <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Brain size={14} style={{ color: '#7c5efa' }} /> Einstein Skill Ledger</h2>
                <div className="space-y-2">
                  {overview.skill_ledger.map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-white w-40 truncate">{s.skill}</span>
                      <MiniBar value={s.confidence || 0} max={1} color="linear-gradient(90deg, #7c5efa, #00d4b8)" />
                      <span className="text-xs font-bold w-10 text-right" style={{ color: '#a68eff' }}>{((s.confidence || 0) * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Einstein Analysis Report */}
            {(report || overview?.latest_report) && (() => {
              const r = report || overview?.latest_report;
              return (
                <div className="llmc-card p-5">
                  <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Zap size={14} style={{ color: '#00d4b8' }} /> Einstein Analysis Report</h2>
                  {r.autoresearch_cycle_summary && (
                    <p className="text-sm mb-4 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{r.autoresearch_cycle_summary}</p>
                  )}
                  {r.optimization_recommendations?.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#00d4b8' }}>Optimization Recommendations</div>
                      {r.optimization_recommendations.map((rec, i) => (
                        <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(0,212,184,0.05)', border: '1px solid rgba(0,212,184,0.15)' }}>
                          <div className="text-xs font-semibold text-white">{rec.area}</div>
                          <div className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{rec.action}</div>
                          {rec.expected_lift && <div className="text-[10px] mt-0.5" style={{ color: '#3dffa0' }}>Expected: {rec.expected_lift}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {!overview && posts.length === 0 && (
              <div className="llmc-card p-10 text-center">
                <TrendingUp size={36} className="mx-auto mb-3 opacity-20" style={{ color: '#00d4b8' }} />
                <p className="text-sm font-bold text-white mb-1">No performance data yet</p>
                <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Schedule posts via the Content Studio or Scheduler Board, then track engagement here.</p>
              </div>
            )}
          </>
        )}

        {/* ── POST TRACKER TAB ── */}
        {activeTab === 'posts' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Track engagement metrics for your published posts. Enter data after publishing.</p>
            </div>
            {posts.length === 0 ? (
              <div className="llmc-card p-8 text-center">
                <Calendar size={28} className="mx-auto mb-2 opacity-20" style={{ color: '#00d4b8' }} />
                <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No scheduled posts found. Create posts in the Scheduler Board or Content Studio.</p>
              </div>
            ) : posts.map(post => {
              const t = tracking[post.id] || {};
              const eng = post.engagement || {};
              const platformColor = PLATFORM_COLORS[post.platform] || '#aaa';
              return (
                <div key={post.id} className="llmc-card p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${platformColor}15`, border: `1px solid ${platformColor}30` }}>
                      <span className="text-[9px] font-black" style={{ color: platformColor }}>{(post.platform || 'P').slice(0, 2)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-xs font-bold text-white truncate flex-1">{post.title || 'Untitled post'}</div>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: post.status === 'published' ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.07)', color: post.status === 'published' ? '#3dffa0' : 'rgba(255,255,255,0.4)' }}>
                          {post.status}
                        </span>
                      </div>
                      <div className="text-[10px] mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        {post.platform} · {post.scheduled_at ? new Date(post.scheduled_at).toLocaleDateString() : '—'}
                      </div>
                      {/* Engagement metrics (existing) */}
                      {(eng.views > 0 || eng.likes > 0) && (
                        <div className="flex items-center gap-3 mb-2">
                          {[['Views', eng.views, '#00d4b8'], ['Likes', eng.likes, '#f97316'], ['Clicks', eng.clicks, '#3dffa0'], ['Shares', eng.shares, '#a68eff']].map(([lbl, val, clr]) => (
                            <div key={lbl} className="text-center">
                              <div className="text-xs font-black" style={{ color: clr }}>{val || 0}</div>
                              <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{lbl}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Track engagement form */}
                      {tracking[post.id] !== undefined ? (
                        <div className="grid grid-cols-4 gap-1.5">
                          {['views', 'likes', 'clicks', 'shares'].map(field => (
                            <div key={field}>
                              <div className="text-[9px] mb-0.5 capitalize" style={{ color: 'rgba(255,255,255,0.3)' }}>{field}</div>
                              <input type="number" min="0"
                                value={t[field] || ''} onChange={e => setTracking(prev => ({ ...prev, [post.id]: { ...prev[post.id], [field]: e.target.value } }))}
                                className="llmc-input w-full px-2 py-1 text-xs text-center" placeholder="0"
                                data-testid={`track-${post.id}-${field}`} />
                            </div>
                          ))}
                          <div className="col-span-4 flex gap-2 mt-1">
                            <button onClick={() => saveEngagement(post.id)} disabled={savingTrack === post.id}
                              data-testid={`save-engagement-${post.id}`}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold hover:opacity-80 disabled:opacity-40"
                              style={{ background: 'rgba(61,255,160,0.15)', color: '#3dffa0' }}>
                              {savingTrack === post.id ? <RefreshCw size={10} className="animate-spin" /> : <CheckCircle size={10} />} Save
                            </button>
                            <button onClick={() => setTracking(prev => { const n = { ...prev }; delete n[post.id]; return n; })}
                              className="text-xs px-2 py-1.5 rounded-xl hover:opacity-80"
                              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)' }}>
                              <X size={10} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setTracking(prev => ({ ...prev, [post.id]: eng }))}
                          data-testid={`track-engagement-${post.id}`}
                          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-xl hover:opacity-80"
                          style={{ background: 'rgba(0,212,184,0.08)', border: '1px solid rgba(0,212,184,0.2)', color: '#00d4b8' }}>
                          <BarChart2 size={10} /> Track Engagement
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── AUTO-PILOT TAB ── */}
        {activeTab === 'autopilot' && (
          <div className="space-y-4">
            <div className="llmc-card p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: autopilot.enabled ? 'rgba(61,255,160,0.15)' : 'rgba(255,255,255,0.06)', border: `1px solid ${autopilot.enabled ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                  <Zap size={16} style={{ color: autopilot.enabled ? '#3dffa0' : 'rgba(255,255,255,0.4)' }} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">Auto-Pilot Mode</div>
                  <div className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>Automatically generate & queue content daily based on your niche</div>
                </div>
                {/* Toggle switch */}
                <button onClick={() => setAutopilot(ap => ({ ...ap, enabled: !ap.enabled }))}
                  data-testid="autopilot-toggle"
                  className="relative w-12 h-6 rounded-full transition-all"
                  style={{ background: autopilot.enabled ? '#3dffa0' : 'rgba(255,255,255,0.15)' }}>
                  <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                    style={{ left: autopilot.enabled ? 28 : 2 }} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Niche */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Content Niche</label>
                  <select value={autopilot.niche} onChange={e => setAutopilot(ap => ({ ...ap, niche: e.target.value }))}
                    className="llmc-input w-full px-3 py-2 text-sm" data-testid="autopilot-niche">
                    {['AI & Technology', 'AI & Cybersecurity', 'B2B SaaS', 'Leadership', 'Marketing & Growth',
                      'Future of Work', 'Entrepreneurship', 'Data Science', 'Product Management'].map(n => (
                      <option key={n}>{n}</option>
                    ))}
                  </select>
                </div>

                {/* Platforms */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Platforms</label>
                  <div className="flex flex-wrap gap-1.5">
                    {['LinkedIn', 'Twitter/X', 'Instagram', 'TikTok', 'Meta (Facebook)'].map(pl => {
                      const selected = autopilot.platforms.includes(pl);
                      return (
                        <button key={pl} onClick={() => setAutopilot(ap => ({
                          ...ap, platforms: selected ? ap.platforms.filter(p => p !== pl) : [...ap.platforms, pl]
                        }))}
                          className="text-xs px-3 py-1.5 rounded-xl font-semibold transition-all"
                          style={{ background: selected ? `${PLATFORM_COLORS[pl]}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${selected ? PLATFORM_COLORS[pl] + '50' : 'rgba(255,255,255,0.08)'}`, color: selected ? PLATFORM_COLORS[pl] : 'rgba(255,255,255,0.4)' }}>
                          {pl}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Posts per day + auto-approve */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Posts per day</label>
                    <select value={autopilot.posts_per_day} onChange={e => setAutopilot(ap => ({ ...ap, posts_per_day: +e.target.value }))}
                      className="llmc-input w-full px-3 py-2 text-sm" data-testid="autopilot-posts-per-day">
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} post{n > 1 ? 's' : ''}/day</option>)}
                    </select>
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Auto-Approve</label>
                    <button onClick={() => setAutopilot(ap => ({ ...ap, auto_approve: !ap.auto_approve }))}
                      data-testid="autopilot-auto-approve"
                      className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
                      style={{ background: autopilot.auto_approve ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${autopilot.auto_approve ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.08)'}`, color: autopilot.auto_approve ? '#3dffa0' : 'rgba(255,255,255,0.4)' }}>
                      <CheckCircle size={12} /> {autopilot.auto_approve ? 'Auto-approve ON' : 'Manual review'}
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3 pt-2" style={{ borderTop: '1px solid var(--llmc-border)' }}>
                  <button onClick={saveAutopilot} disabled={apLoading} data-testid="save-autopilot-btn"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold hover:opacity-80 disabled:opacity-40"
                    style={{ background: 'rgba(0,212,184,0.15)', border: '1px solid rgba(0,212,184,0.3)', color: '#00d4b8' }}>
                    {apLoading ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />} Save Settings
                  </button>
                  <button onClick={runAutopilotNow} disabled={loadingAP} data-testid="run-autopilot-btn"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold hover:opacity-80 disabled:opacity-40"
                    style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
                    {loadingAP ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />}
                    {loadingAP ? 'Generating...' : 'Run Auto-Pilot Now'}
                  </button>
                </div>
              </div>
            </div>

            {/* Auto-pilot result */}
            {apResult && (
              <div className="llmc-card p-4" style={{ border: `1px solid ${apResult.success ? 'rgba(61,255,160,0.3)' : 'rgba(255,80,80,0.3)'}` }}>
                <div className="flex items-center gap-2 mb-3">
                  {apResult.success ? <CheckCircle size={14} style={{ color: '#3dffa0' }} /> : <AlertCircle size={14} style={{ color: '#ff5050' }} />}
                  <span className="text-xs font-bold text-white">{apResult.message}</span>
                </div>
                {apResult.posts?.length > 0 && (
                  <div className="space-y-1.5">
                    {apResult.posts.map((p, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        <ChevronRight size={10} style={{ color: '#3dffa0' }} />
                        <span className="font-semibold" style={{ color: PLATFORM_COLORS[p.platform] || '#aaa' }}>{p.platform}</span>
                        <span className="flex-1 truncate">{p.title}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' }}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* How it works */}
            <div className="llmc-card p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--llmc-text-muted)' }}>How Auto-Pilot Works</div>
              <div className="space-y-2">
                {[
                  { icon: Brain, text: 'Einstein analyzes trending topics in your niche using LLM intelligence', color: '#a68eff' },
                  { icon: Sparkles, text: 'Generates platform-optimized posts in your brand voice (from Settings profile)', color: '#f97316' },
                  { icon: Calendar, text: 'Queues posts for review or auto-approves and schedules them for optimal times', color: '#60a5fa' },
                  { icon: TrendingUp, text: 'Einstein learns from engagement data to improve future recommendations', color: '#3dffa0' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}12` }}>
                      <item.icon size={11} style={{ color: item.color }} />
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
